<script>
  import { onMount } from "svelte";
  import RightPane from "../components/Panes/RightPane.svelte";
  import pb from "../lib/pocketbase";
  import * as XLSX from 'xlsx';
  import { toasts } from 'svelte-toasts';
  import { DASHBOARD_CONFIG } from '../config.js';

  // State variables
  let timelineData = [];
  let isLoading = false;
  let error = null;
  let currentPage = 1;
  let itemsPerPage = 20;
  let totalItems = 0;
  let totalPages = 1;
  let expandedRows = new Set(); // Track which rows are expanded

  // Search and filter variables
  let searchQuery = "";
  let selectedDeliveryMethod = 'all';
  let uniqueDeliveryMethods = [];
  let selectedStatus = 'all';

  // Date range variables
  let startDate = new Date();
  let endDate = new Date();
  startDate.setDate(startDate.getDate() - 1); // Set to yesterday by default
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  // Format dates for input fields
  function formatDateInputLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  let formattedStartDate = formatDateInputLocal(startDate);
  let formattedEndDate = formatDateInputLocal(endDate);

  // Get today's date for max value of date inputs
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const maxDate = today.toISOString().split('T')[0];

  // Right pane state
  let selectedItem = null;
  let showRightPane = false;

  // Export state
  let isExporting = false;

  // Status translations and colors
  const statusTranslations = {
    NOT_READY: 'No listo',
    PACKING: 'En empaque',
    PARTIALLY_READY: 'Parcialmente listo',
    READY: 'Listo',
    ON_ROUTE: 'En ruta',
    DELIVERED: 'Entregado',
    RETURNED: 'Devuelto',
    DEFAULT: 'Desconocido'
  };

  const statusColors = {
    NOT_READY: 'bg-red-200 border-red-300 text-red-700',
    PACKING: 'bg-yellow-500 border-yellow-600 text-yellow-900',
    PARTIALLY_READY: 'bg-orange-200 border-orange-300 text-orange-700',
    READY: 'bg-teal-200 border-teal-300 text-teal-700',
    ON_ROUTE: 'bg-lightBlue-200 border-lightBlue-300 text-lightBlue-700',
    DELIVERED: 'bg-emerald-200 border-emerald-300 text-emerald-700',
    RETURNED: 'bg-red-200 border-red-300 text-red-700',
    DEFAULT: 'bg-gray-100 border-gray-300 text-gray-700',
  };

  function getStatusTranslation(status) {
    return statusTranslations[status] || statusTranslations.DEFAULT;
  }

  function getStatusColorClass(status) {
    return statusColors[status] || statusColors.DEFAULT;
  }

  // Format date for display
  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + `, ${String(hours).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')} ${ampm}`;
  }

  // Format currency
  function formatCurrency(amount) {
    if (!amount) return 'Q0.00';
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ'
    }).format(amount);
  }

  // Format time difference from seconds
  function formatTimeFromSeconds(seconds) {
    if (!seconds || seconds <= 0) {
      return 'En proceso';
    }
    
    const diffMinutes = Math.floor(seconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h ${diffMinutes % 60}m`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Load timeline data from PocketBase view
  async function loadTimelineData() {
    isLoading = true;
    error = null;

    try {
      // Build filter string
      let filter = '';
      const filters = [];

      // Date range filter
      if (formattedStartDate && formattedEndDate) {
        const startDateStr = new Date(formattedStartDate).toISOString();
        const endDateStr = new Date(formattedEndDate + 'T23:59:59.999Z').toISOString();
        filters.push(`created >= "${startDateStr}" && created <= "${endDateStr}"`);
      }

      // Search filter
      if (searchQuery && searchQuery.trim()) {
        const search = searchQuery.trim();
        const safeSearch = search.replace(/[\\\"]/g, '\\$&');
        filters.push(`(numero ~ "${safeSearch}" || nombre ~ "${safeSearch}" || observ ~ "${safeSearch}" || delivery_method ~ "${safeSearch}")`);
      }

             // Delivery method filter
       if (selectedDeliveryMethod !== 'all') {
         filters.push(`delivery_method = "${selectedDeliveryMethod}"`);
       }

       if (selectedStatus !== 'all') {
         filters.push(`sta = "${selectedStatus}"`);
       }



      if (filters.length > 0) {
        filter = filters.join(' && ');
      }

      // Get total count first
      const countResult = await pb.collection('egreso_timeline').getList(1, 1, {
        filter,
        $autoCancel: false
      });
      totalItems = countResult.totalItems;
      totalPages = Math.ceil(totalItems / itemsPerPage);

      // Ensure current page is valid
      if (currentPage > totalPages) {
        currentPage = totalPages || 1;
      }

      // Get paginated data
      const result = await pb.collection('egreso_timeline').getList(currentPage, itemsPerPage, {
        filter,
        sort: '-created',
        $autoCancel: false
      });

                   timelineData = result.items || [];

      // Update unique delivery methods
      updateUniqueDeliveryMethods();

      if (timelineData.length === 0) {
        showToast('No se encontraron registros para los criterios seleccionados', 'info');
      }

    } catch (err) {
      error = err.message || "Error al cargar los datos";
      console.error("Error in loadTimelineData:", err);
      showToast(error, 'error');
      timelineData = [];
    } finally {
      isLoading = false;
    }
  }

  // Update unique delivery methods
  function updateUniqueDeliveryMethods() {
    const methods = new Set();
    methods.add('all');
    timelineData.forEach(item => {
      if (item.delivery_method) {
        methods.add(item.delivery_method);
      }
    });
    uniqueDeliveryMethods = Array.from(methods);
  }

  // Change page function
  function changePage(newPage) {
    if (newPage >= 1 && newPage <= totalPages) {
      currentPage = newPage;
      loadTimelineData();
    }
  }

  // Handle item click
  function handleItemClick(item) {
    selectedItem = item;
    showRightPane = true;
  }

  // Toggle row expansion
  function toggleRowExpansion(egresoId) {
    if (expandedRows.has(egresoId)) {
      expandedRows.delete(egresoId);
    } else {
      expandedRows.add(egresoId);
    }
    expandedRows = new Set(expandedRows); // Force reactivity
  }



  // Close right pane
  function closeRightPane() {
    showRightPane = false;
    selectedItem = null;
  }

  // Show toast notification
  function showToast(message, type = 'success') {
    toasts.add({
      title: type === 'success' ? 'Éxito' : 'Error',
      description: message,
      duration: 3000,
      placement: 'bottom-right',
      type: type,
      theme: 'dark',
      showProgress: true
    });
  }

  // Export to Excel
  async function exportToExcel() {
    isExporting = true;
    try {
      // Build filter for export (same as loadTimelineData)
      let filter = '';
      const filters = [];

      if (formattedStartDate && formattedEndDate) {
        const startDateStr = new Date(formattedStartDate).toISOString();
        const endDateStr = new Date(formattedEndDate + 'T23:59:59.999Z').toISOString();
        filters.push(`created >= "${startDateStr}" && created <= "${endDateStr}"`);
      }

      if (searchQuery && searchQuery.trim()) {
        const search = searchQuery.trim();
        const safeSearch = search.replace(/[\\\"]/g, '\\$&');
        filters.push(`(numero ~ "${safeSearch}" || nombre ~ "${safeSearch}" || observ ~ "${safeSearch}" || delivery_method ~ "${safeSearch}")`);
      }

      if (selectedDeliveryMethod !== 'all') {
        filters.push(`delivery_method = "${selectedDeliveryMethod}"`);
      }

      if (filters.length > 0) {
        filter = filters.join(' && ');
      }

      // Get all data for export
      const result = await pb.collection('egreso_timeline').getFullList({
        filter,
        sort: '-created',
        $autoCancel: false
      });



      const wb = XLSX.utils.book_new();
      const wsData = [
        [
          'Número', 'Nombre', 'Fecha', 'Estado', 'Método de Entrega', 'Total', 
          'Detalles', 'Pickers', 'Tiempo Entrega', 'Recibe', 'Teléfono', 'Creado'
        ]
      ];

      const rows = result.map(item => {
        return [
          item.numero || '',
          item.nombre || '',
          item.fecha ? formatDate(item.fecha) : '',
          getStatusTranslation(item.sta),
          item.delivery_method || '',
          formatCurrency(item.total),
          item.totalDetalles || 0,
          item.totalPickers || 0,
          formatTimeFromSeconds(item.log_time_diff_seconds),
          item.nombre_recibe || '',
          item.tel_recibe || '',
          item.created ? formatDate(item.created) : ''
        ];
      });

      wsData.push(...rows);
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      const colWidths = [
        { wch: 15 }, // Número
        { wch: 30 }, // Nombre
        { wch: 20 }, // Fecha
        { wch: 20 }, // Estado
        { wch: 20 }, // Método de Entrega
        { wch: 15 }, // Total
        { wch: 15 }, // Detalles
        { wch: 15 }, // Pickers
        { wch: 20 }, // Tiempo Entrega
        { wch: 25 }, // Recibe
        { wch: 15 }, // Teléfono
        { wch: 20 }  // Creado
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Timeline');
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `timeline_export_${currentDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      showToast('Archivo Excel generado exitosamente');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showToast('Error al generar el archivo Excel', 'error');
    } finally {
      isExporting = false;
    }
  }

  // Reactive statements
  $: if (searchQuery !== undefined || formattedStartDate !== undefined || formattedEndDate !== undefined || selectedDeliveryMethod !== undefined || selectedStatus !== undefined) {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      currentPage = 1;
      loadTimelineData();
    }, 400);
  }

  let searchTimeout;

  onMount(() => {
    loadTimelineData();
  });
</script>

<style>
  .timeline-container {
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }

  .table-container {
    overflow-x: auto;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .timeline-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  .timeline-table th {
    background-color: #f8f9fa;
    padding: 12px 8px;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .timeline-table td {
    padding: 12px 8px;
    border-bottom: 1px solid #e5e7eb;
    vertical-align: top;
  }

  .timeline-table tbody tr:hover {
    background-color: #f9fafb;
    cursor: pointer;
  }

  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    border-width: 1px;
    border-style: solid;
  }

  .search-container {
    margin-bottom: 1.5rem;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: border-color 0.15s ease;
  }

  .search-input:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
  }

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    gap: 0.5rem;
  }

  .pagination-btn {
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    background: white;
    border: 1px solid #ddd;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pagination-btn:hover {
    background: #f8f9fa;
    border-color: #c5c9d0;
  }

  .pagination-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .export-excel-btn {
    background-color: var(--color-blue-600, #2563eb);
  }
  
  .export-excel-btn:hover {
    background-color: var(--color-blue-700, #1d4ed8);
  }

  .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }

  .text-truncate:hover {
    white-space: normal;
    word-wrap: break-word;
  }

  .expand-btn {
    transition: all 0.2s ease;
  }

  .expand-btn:hover {
    background-color: #e5e7eb;
  }

  /* Toast styles */
  :global(.svelte-toasts) {
    height: auto !important;
  }
  
  :global(.svelte-toast) {
    margin: 0.5em;
    padding: 0.75em 1em;
    border-radius: 0.25em;
    font-size: 0.875rem;
  }
  
  :global(.svelte-toast.success) {
    background: #10B981;
    color: white;
  }
  
  :global(.svelte-toast.error) {
    background: #EF4444;
    color: white;
  }
</style>

<div class="relative md:pt-6 pb-4 flex flex-col justify-between items-start">
  <div class="timeline-container w-full">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Trazabilidad de pedidos</h2>
      <button 
        on:click={exportToExcel}
        class="export-excel-btn px-4 py-2 text-white rounded transition-colors"
        disabled={isExporting}
      >
        {#if isExporting}
          <i class="fas fa-spinner fa-spin mr-2"></i>Preparando datos...
        {:else}
          <i class="fas fa-file-excel mr-2"></i>Exportar a Excel
        {/if}
      </button>
    </div>

    <!-- Search and filters -->
    <div class="search-container">
      <div class="flex flex-col md:flex-row gap-4 mb-4">
        <div class="flex-1">
          <label class="text-sm text-gray-600 mb-1">Buscar</label>
          <div class="relative">
            <span class="search-icon">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input 
              type="text" 
              bind:value={searchQuery} 
              placeholder="Número, nombre, observaciones..."
              class="search-input"
            />
            {#if searchQuery}
              <button 
                class="absolute right-16 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                on:click={() => { searchQuery = ""; }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            {/if}
          </div>
        </div>
        <div class="flex gap-4">
          <div class="flex flex-col">
            <label class="text-sm text-gray-600 mb-1">Método de entrega</label>
            <select 
              bind:value={selectedDeliveryMethod}
              class="border rounded px-3 py-2 text-sm"
            >
              {#each uniqueDeliveryMethods as method}
                <option value={method}>
                  {method === 'all' ? 'Todos' : method}
                </option>
              {/each}
            </select>
          </div>
          <div class="flex flex-col">
            <label class="text-sm text-gray-600 mb-1">Estado</label>
            <select 
              bind:value={selectedStatus}
              class="border rounded px-3 py-2 text-sm"
            >
              <option value="all">Todos</option>
              {#each DASHBOARD_CONFIG.displayStatuses as status}
                <option value={status}>
                  {getStatusTranslation(status)}
                </option>
              {/each}
            </select>
          </div>
          <div class="flex flex-col">
            <label class="text-sm text-gray-600 mb-1">Desde</label>
            <input 
              type="date" 
              bind:value={formattedStartDate}
              max={formattedEndDate}
              class="border rounded px-3 py-2 text-sm"
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm text-gray-600 mb-1">Hasta</label>
            <input 
              type="date" 
              bind:value={formattedEndDate}
              min={formattedStartDate}
              max={maxDate}
              class="border rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>
    </div>

    {#if isLoading}
      <div class="p-4 text-center">
        <p>Cargando datos...</p>
      </div>
    {:else if error}
      <div class="p-4 text-center text-red-500">
        <p>{error}</p>
      </div>
    {:else if timelineData.length === 0}
      <div class="p-4 text-center">
        <p>No se encontraron registros para mostrar.</p>
      </div>
    {:else}
      <!-- Summary -->
      <div class="mb-4 text-sm text-gray-600">
        Mostrando {timelineData.length} de {totalItems} registros
        {#if searchQuery}
          <span>para "{searchQuery}"</span>
        {/if}
      </div>

             <!-- Table -->
       <div class="table-container">
         <table class="timeline-table">
           <thead>
             <tr>
               <th style="width: 40px;"></th>
               <th>Número</th>
               <th>Nombre</th>
               <th>Fecha</th>
               <th>Estado</th>
               <th>Método de Entrega</th>
               <th>Total</th>
               <th>Detalles</th>
               <th>Pickers</th>
               <th>Tiempo Entrega</th>
               <th>Recibe</th>
               <th>Teléfono</th>
               <th>Creado</th>
             </tr>
           </thead>
           <tbody>
             {#each timelineData as item}
               <!-- Main row -->
               <tr class="hover:bg-gray-50 cursor-pointer" on:click={() => handleItemClick(item)}>
                 <td class="text-center">
                   <button 
                     class="expand-btn p-1 rounded hover:bg-gray-200 transition-colors"
                     on:click|stopPropagation={() => toggleRowExpansion(item.id)}
                   >
                     {#if expandedRows.has(item.id)}
                       <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                       </svg>
                     {:else}
                       <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                       </svg>
                     {/if}
                   </button>
                 </td>
                 <td class="font-medium">{item.numero || '-'}</td>
                 <td>
                   <div class="text-truncate" title={item.nombre}>
                     {item.nombre || '-'}
                   </div>
                   {#if item.observ}
                     <div class="text-xs text-gray-500 text-truncate" title={item.observ}>
                       {item.observ}
                     </div>
                   {/if}
                 </td>
                 <td>{item.fecha ? formatDate(item.fecha) : '-'}</td>
                 <td>
                   <span class="status-badge {getStatusColorClass(item.sta)}">
                     {getStatusTranslation(item.sta)}
                   </span>
                 </td>
                 <td>{item.delivery_method || '-'}</td>
                 <td class="font-medium">{formatCurrency(item.total)}</td>
                 <td class="text-center">{item.totalDetalles || 0}</td>
                 <td>
                   <div class="text-center">
                     <span class="font-medium">{item.totalPickers || 0}</span>
                     {#if item.pickers}
                       <div class="text-xs text-gray-500 text-truncate" title={item.pickers}>
                         {item.pickers}
                       </div>
                     {/if}
                   </div>
                 </td>
                 <td class="text-center">
                   {#if item.log_time_diff_seconds && item.log_time_diff_seconds > 0}
                     <span class="font-medium text-green-600">
                       {formatTimeFromSeconds(item.log_time_diff_seconds)}
                     </span>
                     <div class="text-xs text-gray-500">
                       NOT_READY → DELIVERED
                     </div>
                   {:else}
                     <span class="font-medium text-gray-500">
                       En proceso
                     </span>
                   {/if}
                 </td>
                 <td>{item.nombre_recibe || '-'}</td>
                 <td>{item.tel_recibe || '-'}</td>
                 <td>{item.created ? formatDate(item.created) : '-'}</td>
               </tr>
               
               <!-- Expanded details row -->
               {#if expandedRows.has(item.id)}
                 <tr class="bg-gray-50">
                   <td colspan="13" class="p-0">
                     <div class="p-4">
                       <h4 class="text-sm font-semibold text-gray-700 mb-3">Detalles del Pedido</h4>
                       <div class="grid grid-cols-2 gap-4">
                         <div>
                           <p><strong>ID:</strong> {item.id}</p>
                           <p><strong>Documento:</strong> {item.documento || '-'}</p>
                           <p><strong>NIT:</strong> {item.nit || '-'}</p>
                           <p><strong>Observaciones:</strong> {item.observ || '-'}</p>
                           <p><strong>Sucursal:</strong> {item.sucursal || '-'}</p>
                           <p><strong>Creado por:</strong> {item.created_by || '-'}</p>
                         </div>
                         <div>
                           <p><strong>UUID:</strong> {item.uuid || '-'}</p>
                           <p><strong>Referencia Target:</strong> {item.target_ref || '-'}</p>
                           <p><strong>Referencia Delivery:</strong> {item.delivery_ref || '-'}</p>
                           <p><strong>Vehículo:</strong> {item.vehiculo || '-'}</p>
                           <p><strong>Transporte:</strong> {item.transporte || '-'}</p>
                           <p><strong>Tiempo de Entrega:</strong> {formatTimeFromSeconds(item.log_time_diff_seconds)}</p>
                         </div>
                       </div>
                     </div>
                   </td>
                 </tr>
               {/if}
             {/each}
           </tbody>
         </table>
       </div>

      <!-- Pagination -->
      {#if totalPages > 1}
        <div class="pagination">
          <button 
            class="pagination-btn" 
            on:click={() => changePage(1)} 
            disabled={currentPage === 1}
          >
            &laquo;
          </button>
          
          <button 
            class="pagination-btn" 
            on:click={() => changePage(currentPage - 1)} 
            disabled={currentPage === 1}
          >
            &lt;
          </button>
          
          <!-- Page numbers -->
          {#if totalPages <= 7}
            {#each Array(totalPages) as _, i}
              <button 
                class="pagination-btn {currentPage === i + 1 ? 'active' : ''}" 
                on:click={() => changePage(i + 1)}
              >
                {i + 1}
              </button>
            {/each}
          {:else}
            <!-- First page -->
            <button 
              class="pagination-btn {currentPage === 1 ? 'active' : ''}" 
              on:click={() => changePage(1)}
            >
              1
            </button>
            
            <!-- Ellipsis if needed -->
            {#if currentPage > 3}
              <span class="pagination-btn">...</span>
            {/if}
            
            <!-- Pages around current page -->
            {#each Array(3) as _, i}
              {#if currentPage - 1 + i > 1 && currentPage - 1 + i < totalPages}
                <button 
                  class="pagination-btn {currentPage === currentPage - 1 + i ? 'active' : ''}" 
                  on:click={() => changePage(currentPage - 1 + i)}
                >
                  {currentPage - 1 + i}
                </button>
              {/if}
            {/each}
            
            <!-- Ellipsis if needed -->
            {#if currentPage < totalPages - 2}
              <span class="pagination-btn">...</span>
            {/if}
            
            <!-- Last page -->
            <button 
              class="pagination-btn {currentPage === totalPages ? 'active' : ''}" 
              on:click={() => changePage(totalPages)}
            >
              {totalPages}
            </button>
          {/if}
          
          <button 
            class="pagination-btn" 
            on:click={() => changePage(currentPage + 1)} 
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
          
          <button 
            class="pagination-btn" 
            on:click={() => changePage(totalPages)} 
            disabled={currentPage === totalPages}
          >
            &raquo;
          </button>
        </div>
      {/if}
    {/if}
  </div>
</div>

<!-- Right Pane -->
{#if showRightPane && selectedItem}
  <RightPane 
    selectedItem={selectedItem} 
    closePane={closeRightPane}
    status={selectedItem.sta}
  />
{/if}